<template>
  <profile-modal-base
    :is-open="showModal"
    data-test="edit-profile-modal"
    @close="close"
  >
    <template #header>
      <profile-modal-header
        data-test="edit-profile-modal"
        :header-text="'Edit Profile'"
        :back-target="'profileShowModal'"
      >
      </profile-modal-header>
    </template>
    <modal-row-group>
      <div
        class="flexbox-row modal-row avatar-row"
        data-test="avatar-row"
      >
        <div class="avatar-container" data-test="avatar-container">
          <img
            v-if="avatarThumbUrl"
            :src="avatarThumbUrl"
            alt="avatar"
            class="avatar"
          />
          <span v-else-if="firstInitial" class="display-xs font-semibold first-initial">
            {{ firstInitial }}
          </span>
          <span v-else>
            <icon name="avatar" />
          </span>
        </div>
        <div class="flex-1">
          <div class="text-xs text-tertiary">
            This will be displayed in discussions
          </div>
          <div class="avatar-options">
            <button
              :disabled="!avatarThumbUrl"
              class="button-reset text-sm font-semibold button-tertiary-fg delete-button"
              @click="deleteAvatar"
            >
              Delete
            </button>
            <button
              class="button-reset button-link text-sm font-semibold"
              @click="triggerInput"
            >
              Update
            </button>
            <input
              ref="avatarInput"
              accept="image/*"
              type="file"
              class="button-reset button-link text-sm font-semibold"
              style="display: none;"
              @change="updateAvatar"
            />
          </div>
        </div>
      </div>
      <div
        class="button-reset flexbox-row modal-row"
        data-test="edit-name-row"
      >
        <span class="text-md font-medium text-primary">
          Name
        </span>
        <span class="flex-1" />
        <span
          class="text-md font-regular"
          data-test="first-name-text"
        >
          <subtle-text-input
            :value="firstName"
            :placeholder="'first name'"
            @input="updateFirstName"
          ></subtle-text-input>
        </span>
      </div>
      <div
        v-if="showTestDate"
        class="button-reset flexbox-row modal-row"
        data-test="test-date-row"
      >
        <span class="text-md font-medium text-primary">
          Test Date
        </span>
        <span class="flex-1" />
        <span class="text-md font-regular">
          <subtle-text-input
            ref="testDateInput"
            :value="testDate"
            :placeholder="'mm/dd/yyyy'"
            @input="updateTestDate"
          >
          </subtle-text-input>
        </span>
      </div>
    </modal-row-group>
    <modal-row-group
      v-if="showDailyGoal"
      data-test="daily-goal-row-group"
    >
      <div class="modal-row daily-goal-row">
        <div class="flexbox-row flex-1">
          <span class="text-md font-medium text-primary">
            Daily Goal
          </span>
          <span class="flex-1" />
          <span class="text-md font-regular">
            {{ numberInput }}
            Questions
          </span>
        </div>
        <div>
          <input-range
            v-model="numberInput"
            :min="1"
            :max="maxQuestions"
            :step="1"
            class="full-width"
            :show-label="false"
          />
        </div>
      </div>
    </modal-row-group>
  </profile-modal-base>
</template>

<script>
import AvatarMixin from '@/mixins/avatar';
import InputRange from '@components/singletons/InputRange';
import ProfileModalBase from './ProfileModalBase.vue';
import ProfileModalHeader from './ProfileModalHeader.vue';
import ProfileModalMixin from '@/mixins/profileModal';
import SubtleTextInput from '@/components/singletons/SubtleTextInput.vue';
import ModalRowGroup from './ModalRowGroup.vue';
import { SET_GOAL_KEY, setChecklistData } from '@/utils/checklist';
const MAX_QUESTIONS = 50;

const MODAL_NAME = 'editProfileModal';

export default {
  name: 'EditProfileModal',
  components: {
    InputRange,
    ModalRowGroup,
    ProfileModalBase,
    ProfileModalHeader,
    SubtleTextInput,
  },
  mixins: [AvatarMixin, ProfileModalMixin],
  data() {
    return {
      maxQuestions: MAX_QUESTIONS,
      numberInput: 20,
      debouncedNumberInput: 20,
      debouncerTimeout: null,
      pushValue: false,
    };
  },
  computed: {
    dailyGoal() {
      return this.$store.getters['session/currentUser']?.userProfile?.studyGoal || 10;
    },
    newDailyGoalValue() {
      return this.numberInput !== this.dailyGoal ? this.numberInput : null;
    },
    showDailyGoal() {
      return !this.isAse();
    },
    showModal() {
      return this.$store.getters['ui/profileModalShown'] === MODAL_NAME;
    },
    showTestDate() {
      return !this.isAse();
    },
  },
  watch: {
    numberInput: {
      handler: function (newVal) {
        if (this.debouncerTimeout) {
          clearTimeout(this.debouncerTimeout);
        }
        this.debouncerTimeout = setTimeout(() => {
          this.debouncedNumberInput = newVal;
          this.debouncerTimeout = null;
          this.updateDailyGoal(this.newDailyGoalValue);
        }, 1000);
      },
      immediate: true,
    },
  },
  created() {
    this.numberInput = this.dailyGoal;
    this.debouncedNumberInput = this.dailyGoal;
  },
  mounted() {
    this.numberInput = this.dailyGoal;
  },
  methods: {
    deleteAvatar() {
      const formData = new FormData();
      formData.append('delete_avatar', true);
      this.$store.dispatch('user/updateAvatar', formData)
        .then(() => {
          this.$notify({
            group: 'notifications',
            text: 'Avatar Deleted',
            type: 'success',
          });
        })
        .catch(e => {
          this.$notify({
            group: 'notifications',
            text: 'Error Deleting Avatar',
            type: 'error',
          });
        });
    },
    triggerInput() {
      this.$refs.avatarInput.click();
    },
    updateAvatar(event) {
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('user[avatar]', file);

      this.$store.dispatch('user/updateAvatar', formData).then(() => {
        this.$notify({
          group: 'notifications',
          text: 'Avatar Updated',
          type: 'success',
        });
      }).catch(e => {
        this.$notify({
          group: 'notifications',
          text: 'Error Updating Avatar',
          type: 'error',
        });
      });
    },
    updateDailyGoal(newGoal) {
      if (newGoal) {
        this.submitting = true;
        this.$store.dispatch('session/updateUserProfile', { studyGoal: newGoal }).then(() => {
          this.$analytics.events.setDailyGoal('Edit Profile Modal');
          this.$analytics.events.setStudyGoal('Set Study Goal', { 'Number Of Questions': newGoal });
          setChecklistData(this.$store, SET_GOAL_KEY, this.$notify);
          this.submitting = false;

          this.$notify({
            group: 'notifications',
            text: 'Study Goal Updated',
            type: 'success',
          });
        }).catch(e => {
          this.$notify({
            group: 'notifications',
            text: 'Error Updating Profile',
            type: 'error',
          });
        });;
      }
    },
    updateFirstName(firstName) {
      if (firstName === this.firstName) {
        return;
      }

      const params = {
        user: {
          first_name: firstName,
        },
      };

      this.$store.dispatch('session/updateUser', params).then(response => {
        this.$notify({
          group: 'notifications',
          text: 'First Name Updated',
          type: 'success',
        });
      }).catch(e => {
        this.firstName = this.prevFirstName;
        this.$notify({
          group: 'notifications',
          text: 'Error Updating Profile',
          type: 'error',
        });
      });
    },
    updateTestDate(testDate) {
      const regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(\d{4})$/;
      if (!regex.test(testDate)) {
        this.resetTestDate();
        this.$notify({
          group: 'notifications',
          text: 'Invalid date format (MM/DD/YYYY)',
          type: 'error',
        });
        return;
      }
      testDate = this.stripLeadingZeros(testDate);

      if (testDate === this.testDate) {
        this.resetTestDate();
        return;
      }

      const [month, day, year] = testDate.split('/');

      const params = {};
      params[this.testDateKey] = `${year}-${month}-${day}`;

      this.$store.dispatch('session/updateUserProfile', params).then(() => {
        this.$notify({
          group: 'notifications',
          text: 'Test Date Updated',
          type: 'success',
        });
      }).catch(e => {
        this.resetTestDate();
        this.$notify({
          group: 'notifications',
          text: 'Error Updating Test Date Profile',
          type: 'error',
        });
      });
    },
    resetTestDate() {
      if (this.$refs.testDateInput) {
          this.$refs.testDateInput.text = this.testDate;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: rem-calc(8);
  padding: 0 rem-calc(20);
  min-height: rem-calc(48);

  &.avatar-row {
    padding: rem-calc(24) rem-calc(20);
    gap: rem-calc(12);
  }
}

.avatar {
  border-radius: 50%;
}

.avatar-options {
  display: flex;
  gap: rem-calc(16);
  min-height: rem-calc(32);
}

.daily-goal-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: rem-calc(16);
}

.delete-button:disabled {
  opacity: 0.5;
}
</style>
